import React, { useState } from 'react';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const CraneCard = ({ crane, onGetValuation, onCompare, isSelected = false }) => {
  const [imageError, setImageError] = useState(false);

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    })?.format(price);
  };

  const formatHours = (hours) => {
    return new Intl.NumberFormat('en-US')?.format(hours);
  };

  const getConditionColor = (grade) => {
    const colors = {
      5: 'text-success bg-success/10',
      4: 'text-primary bg-primary/10',
      3: 'text-warning bg-warning/10',
      2: 'text-error bg-error/10',
      1: 'text-destructive bg-destructive/10'
    };
    return colors?.[grade] || 'text-text-secondary bg-muted';
  };

  const getDOMColor = (days) => {
    if (days <= 30) return 'text-success';
    if (days <= 90) return 'text-warning';
    return 'text-error';
  };

  return (
    <div className={`bg-surface border rounded-lg shadow-card hover:shadow-modal transition-smooth overflow-hidden ${isSelected ? 'border-primary ring-2 ring-primary/20' : 'border-border'}`}>
      {/* Image Section */}
      <div className="relative h-48 bg-muted overflow-hidden">
        <Image
          src={crane?.image}
          alt={crane?.imageAlt}
          className="w-full h-full object-cover"
        />
        
        {/* Overlay Info */}
        <div className="absolute top-3 left-3 flex flex-col space-y-2">
          <div className={`px-2 py-1 rounded-full text-xs font-medium ${getConditionColor(crane?.condition)}`}>
            Grade {crane?.condition}
          </div>
          {crane?.isNew && (
            <div className="px-2 py-1 bg-accent text-accent-foreground rounded-full text-xs font-medium">
              New Listing
            </div>
          )}
        </div>

        {/* Comparison Checkbox */}
        <div className="absolute top-3 right-3">
          <button
            onClick={() => onCompare(crane?.id)}
            className={`w-6 h-6 rounded border-2 flex items-center justify-center transition-smooth ${
              isSelected 
                ? 'bg-primary border-primary' :'bg-white/80 border-white/60 hover:bg-white hover:border-white'
            }`}
          >
            {isSelected && <Icon name="Check" size={14} color="white" />}
          </button>
        </div>
      </div>
      {/* Content Section */}
      <div className="p-4">
        {/* Header */}
        <div className="mb-3">
          <h3 className="font-semibold text-text-primary text-lg leading-tight">
            {crane?.year} {crane?.make} {crane?.model}
          </h3>
          <p className="text-sm text-text-secondary mt-1">{crane?.location}</p>
        </div>

        {/* Key Specifications */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div className="flex items-center space-x-2">
            <Icon name="Clock" size={14} className="text-text-secondary" />
            <span className="text-sm text-text-secondary">
              {formatHours(crane?.hours)} hrs
            </span>
          </div>
          <div className="flex items-center space-x-2">
            <Icon name="Weight" size={14} className="text-text-secondary" />
            <span className="text-sm text-text-secondary">
              {crane?.capacity}t
            </span>
          </div>
          <div className="flex items-center space-x-2">
            <Icon name="Ruler" size={14} className="text-text-secondary" />
            <span className="text-sm text-text-secondary">
              {crane?.boomLength}m boom
            </span>
          </div>
          <div className="flex items-center space-x-2">
            <Icon name="Calendar" size={14} className={getDOMColor(crane?.daysOnMarket)} />
            <span className={`text-sm ${getDOMColor(crane?.daysOnMarket)}`}>
              {crane?.daysOnMarket} DOM
            </span>
          </div>
        </div>

        {/* Pricing Information */}
        <div className="mb-4 p-3 bg-muted rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-text-primary">Market Value Range</span>
            <div className="flex items-center space-x-1">
              <Icon name="TrendingUp" size={12} className="text-success" />
              <span className="text-xs text-success">+2.3%</span>
            </div>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-lg font-semibold text-text-primary">
              {formatPrice(crane?.priceRange?.min)} - {formatPrice(crane?.priceRange?.max)}
            </span>
            <span className="text-sm text-text-secondary">
              Avg: {formatPrice(crane?.priceRange?.avg)}
            </span>
          </div>
          <div className="mt-2 flex items-center space-x-2">
            <div className="flex-1 bg-border rounded-full h-2">
              <div 
                className="bg-primary h-2 rounded-full" 
                style={{ width: `${crane?.confidenceScore}%` }}
              ></div>
            </div>
            <span className="text-xs text-text-secondary">{crane?.confidenceScore}% confidence</span>
          </div>
        </div>

        {/* Dealer Information */}
        <div className="mb-4 pb-3 border-b border-border">
          <div className="flex items-center space-x-2">
            <Icon name="Building2" size={14} className="text-text-secondary" />
            <span className="text-sm font-medium text-text-primary">{crane?.dealer}</span>
          </div>
          <div className="flex items-center space-x-2 mt-1">
            <Icon name="MapPin" size={14} className="text-text-secondary" />
            <span className="text-sm text-text-secondary">{crane?.dealerLocation}</span>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col space-y-2">
          <Button
            variant="default"
            size="sm"
            onClick={() => onGetValuation(crane)}
            iconName="FileText"
            iconPosition="left"
            fullWidth
          >
            Get Full Valuation
          </Button>
          
          <div className="flex space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.location.href = `/crane-valuation-report?id=${crane?.id}`}
              iconName="Eye"
              iconPosition="left"
              className="flex-1"
            >
              View Details
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => console.log('Save crane', crane?.id)}
              iconName="Bookmark"
              className="px-3"
            />
            <Button
              variant="ghost"
              size="sm"
              onClick={() => console.log('Share crane', crane?.id)}
              iconName="Share2"
              className="px-3"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CraneCard;